import { memo, type ReactNode } from 'react';

import { stripEntityTags } from '@/lib/markdown/entityRendering';
import { markdownToPlainTextPreview } from '@/lib/markdown/plainTextPreview';
import type { ProposalListItem } from '@/lib/proposals/core/types';
import { cx } from '@/utils/cx';

import { formatTimeAgo } from './ActionRowItem';

interface ActionTableRowProps {
  action: ProposalListItem;
  /** Source icon / recipient avatar, resolved by the caller. */
  icon: ReactNode;
  /** Plays the entrance animation for rows that just arrived via WebSocket. */
  isNew: boolean;
  /** History rows show resolved_at instead of created_at and no unread dot. */
  isHistory?: boolean;
}

/**
 * ActionTableRow — single-line row content for the AI Actions grouped table.
 *
 * Rendered inside GroupedList's row shell (which owns the checkbox gutter,
 * divider, hover highlight, click activation, and hover actions): icon,
 * unread dot, title, one-line description, right-aligned relative timestamp.
 * The timestamp hides on row hover so the hover actions can occupy its slot
 * without garbling it (GroupedList exposes the row's `group/row` scope).
 */
export const ActionTableRow = memo(function ActionTableRow({
  action,
  icon,
  isNew,
  isHistory = false,
}: ActionTableRowProps) {
  const timestamp = formatTimeAgo(
    isHistory ? (action.resolved_at ?? action.created_at) : action.created_at
  );

  return (
    // items-baseline, not items-center: the title (14px), description (13px)
    // and timestamp (11px) share a 20px line height, but centering their equal
    // boxes leaves their BASELINES ~0.5-1px apart (a smaller font's baseline
    // sits higher inside the same box), which reads as the smaller text
    // floating high. Baseline alignment is what makes mixed-size text sit on
    // one line; measured on the live row: deltas go from -0.5/-1px to 0/0,
    // and `truncate` does not disturb the items' baselines here. Non-text
    // items (icon, unread dot) opt back into vertical centering.
    <div
      className={cx(
        'flex items-baseline gap-2 py-4',
        isNew && 'proposal-enter'
      )}
    >
      <div
        className={cx(
          'flex-shrink-0 self-center flex items-center',
          isNew && 'proposal-icon'
        )}
      >
        {icon}
      </div>
      {!isHistory && action.read_at === null && (
        <span
          aria-label='Unread'
          data-testid='unread-dot'
          className='flex-shrink-0 self-center w-[8px] h-[8px] rounded-full bg-[var(--color-brand-600)]'
        />
      )}
      <h3 className='flex-shrink-0 max-w-[45%] font-semibold text-[14px] leading-[20px] text-[var(--color-text-primary)] truncate'>
        {action.title}
      </h3>
      {/* One line of text, one element: the description renders as a plain
          truncating span at the title's exact 20px line height. It previously
          went through MarkdownContent (div > p + overrides to undo the
          mention-pill line-height), which cost three nested elements and a
          -webkit-box clamp for what is a single-line preview. Markdown syntax
          is flattened to text up front instead of rendered. */}
      {action.description ? (
        <span className='min-w-0 flex-1 truncate text-[13px] leading-[20px] font-normal text-secondary'>
          {markdownToPlainTextPreview(stripEntityTags(action.description))}
        </span>
      ) : (
        <span className='flex-1' />
      )}
      {/* ml widens the description→time gap to 24px total (8px flex gap + 16px) */}
      <span className='flex-shrink-0 ml-4 font-normal text-[11px] leading-[20px] whitespace-nowrap text-fg-quaternary group-hover/row:invisible'>
        {timestamp}
      </span>
    </div>
  );
});
